import type { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { RRule, type Weekday } from 'rrule';
import { Select, type SelectProps, useColorModeValue } from '@/ui';
import { getDateCardinal } from '@/utils/dates';
import { WeekDayNumbers } from '../../constants/recurrence';
import { getNthWeekdayOfMonth } from './lib';

type Props = Omit<SelectProps, 'onChange'> & {
  startDate: DateTime;
  rule: string | null;
  onChange: (weekday: Weekday | null) => void;
};

export const MonthRecurrenceSelect = ({
  startDate,
  onChange,
  rule: ruleString,
  ...props
}: Props) => {
  const { t } = useTranslation('common', { keyPrefix: 'recurrence_select' });
  const textColor = useColorModeValue(
    'customgray.dark',
    'customgray.alsolight'
  );

  const rule = RRule.fromString(ruleString || '');
  const weekdayIndex = getNthWeekdayOfMonth(startDate);

  return (
    <Select
      aria-label={t('month_aria')}
      color={textColor}
      size="sm"
      value={rule.options.bymonthday.length > 0 ? 'none' : 'nth'}
      onChange={(event) => {
        const value = event.currentTarget.value;

        if (value === 'none') {
          onChange(null);
        } else if (value === 'nth') {
          const weekday = WeekDayNumbers[startDate.weekday - 1];
          onChange(weekday.nth(weekdayIndex));
        }
      }}
      {...props}
    >
      <option value="none">{t('month_day', { date: startDate.day })}</option>
      <option value="nth">
        {t('month_nth', {
          index: weekdayIndex,
          nth: getDateCardinal(weekdayIndex),
          date: startDate.toFormat('cccc'),
        })}
      </option>
    </Select>
  );
};
